import { useState } from "react";
import { useAuth } from "../AuthContext";
import { useFleetApi, type EscalationRow } from "./api";

/** Ids typed as "12, 9 14"; null when any token is not a whole number. */
function parseIds(text: string): number[] | null {
  const tokens = text.split(/[\s,]+/).filter(Boolean);
  const ids = tokens.map(Number);
  return ids.every(n => Number.isInteger(n) && n > 0) ? ids : null;
}

interface Props {
  rows: EscalationRow[];
  onDone: () => void;
}

/**
 * Reject every open escalation except the ids kept open. Test traffic is never
 * rejected in bulk, so it is left out of the count.
 */
export default function BulkRejectBar({ rows, onDone }: Props) {
  const { token, logout } = useAuth();
  const { bulkRejectEscalations } = useFleetApi(token, logout);
  const [armed, setArmed] = useState(false);
  const [keepText, setKeepText] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const keep = parseIds(keepText);
  const keepSet = new Set(keep ?? []);
  const count = rows.filter(r => !r.is_test && !keepSet.has(r.id)).length;

  const run = async () => {
    if (!keep || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await bulkRejectEscalations(keep);
      const parts = [`Rejected ${res.rejected.length}.`];
      if (res.skipped_open.length) parts.push(`Kept open: ${res.skipped_open.join(", ")}.`);
      if (res.errors.length) {
        parts.push(
          `Failed: ${res.errors.map(e => `${e.escalation_id} (${e.message})`).join("; ")}.`,
        );
      }
      setNote(parts.join(" "));
      setArmed(false);
      setKeepText("");
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!armed) {
    return (
      <div className="bulk-reject">
        <button
          className="act crit"
          disabled={count === 0}
          onClick={() => { setNote(null); setArmed(true); }}
        >
          Reject all open…
        </button>
        {note && <span className="meta">{note}</span>}
      </div>
    );
  }

  return (
    <div className="bulk-reject">
      <span>Reject {count} open escalation{count === 1 ? "" : "s"}? Keep open:</span>
      <input
        aria-label="Escalation ids to keep open"
        placeholder="ids, e.g. 12, 9"
        value={keepText}
        onChange={e => setKeepText(e.target.value)}
        disabled={busy}
      />
      <button className="act crit" disabled={busy || !keep || count === 0} onClick={() => void run()}>
        {busy ? "Rejecting…" : `Reject ${count}`}
      </button>
      <button className="act" disabled={busy} onClick={() => { setArmed(false); setKeepText(""); }}>
        Cancel
      </button>
      {!keep && <span style={{ color: "var(--crit)" }}>Ids must be whole numbers.</span>}
      {err && <span style={{ color: "var(--crit)" }}>{err}</span>}
    </div>
  );
}
